import type { UserManager } from 'oidc-client-ts';
import { loginStorage } from './storage';

const saveCurrentRoute = () => {
    const initialRoute = [window.location.hash, window.location.search].join('').replace(/^#\/?/u, '');

    const redirectTimestamp = new Date().toISOString();
    loginStorage.saveLoginRedirectTimestamp(redirectTimestamp);
    if (initialRoute) {
        loginStorage.saveRoute(initialRoute);
        // trace('initialRoute: saving initial route', initialRoute);
    }
};

const doSignIn = (userManager: UserManager, isAuthCodeRedirect: boolean) => {
    return isAuthCodeRedirect ? userManager.signinCallback() : userManager.signinSilent();
};

export const attemptInitialSignin = async (
    userManager: UserManager,
    options: {
        redirectUri: string;
        preventRedirect: boolean;
        reportError: (err: unknown) => void;
        extraQueryParams?: Record<string, string | number | boolean>;
    }
) => {
    const isAuthCodeRedirect =
        window.location.href.startsWith(options.redirectUri) && /[?&]code=[^&]*/.test(window.location.search);

    try {
        await redirectIfStoredStateIsMissing(userManager, options.redirectUri);
        const user = await doSignIn(userManager, isAuthCodeRedirect);
        const initialRoute = loginStorage.getRoute();

        // trace('initialRoute lookup', initialRoute);

        if (isAuthCodeRedirect && initialRoute !== null) {
            // trace(`initialRoute: Go to location "${initialRoute}"`);
            window.history.replaceState({}, '', `${window.location.origin}/#/${initialRoute}`);
        } else if (isAuthCodeRedirect) {
            window.history.replaceState({}, '', window.location.origin);
        }
        loginStorage.clear();
        return user;
    } catch (error) {
        // trace('oidc.signinSilent failed, trying page redirect...', error);

        if (options.preventRedirect) {
            console.warn('[feature/login] redirect prevented due to config. Error was', error);
        } else if (isAuthCodeRedirect) {
            // trace('oidc.signinSilent.error', 'redirect prevented due to suspicious signin error', error);
            loginStorage.clear();
            options.reportError(error);
        } else {
            saveCurrentRoute();
            await userManager.signinRedirect({ extraQueryParams: { ...options.extraQueryParams } });
            return undefined;
        }

        // trace('auth problem?', error);
        throw new Error('Need to sign in');
    }
};

// Currently we know of two ways this case might happen:
//
// 1. With multiple tabs of the application:
//     - One tab waits on the login form for some time (more than the stale timeout)
//     - The user than performs a login in a second tab
//    The second tab could then remove the now stale state value of the first tab.
//
// 2. Pressing the back button is another way to trigger this case. Using the back
//    button brings one back to the login form. But the login form still uses
//    the old redirect URL for the already removed state. The user can enter their
//    credentials again and will be redirected to the outdated URL.
//
// There might also be other scenarios to reach this branch.
const redirectIfStoredStateIsMissing = async (userManager: UserManager, redirectUrl: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get('state');

    if (state !== null && (await userManager.settings.stateStore.get(state)) === null) {
        // addBreadcrumbToSentry({
        //   category: 'oauth',
        //   level: 'warning',
        //   message: 'Redirecting due to missing stored state',
        //   data: {
        //     redirectUrl,
        //   },
        // });

        loginStorage.clear();

        // We want a real page reload, which will either:
        //  - perform a silent signin, which succeeds if the user already has a session from another login
        //  - trigger a new redirection that brings us back here with a valid state value.
        // We use replace here to override the redirect URL and prevent the back button to go
        // back to the redirect page.
        window.location.replace(redirectUrl);

        // The window.location.replace call runs in the background. We do not want
        // to continue with the rest of the authorization flow. This would just lead
        // to a thrown error from signinCallback(), which we would see in the monitoring.
        // Therefore, we block this promise to let the navigation happen.
        await new Promise<never>(() => {});
    }
};
